"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Flower2, Droplets, Sun, Scissors, Truck } from "lucide-react";
const stages = [{
  id: 1,
  title: "Giorno 1: Applicazione",
  icon: Droplets,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/solid_dew_application.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/solid_dew_application.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/solid_dew_application.png-wvc-sizes",
    alt: "Applicazione del prodotto Solid Dew nel terreno"
  },
  description: "Inizia il ciclo di protezione: i granuli vengono integrati nel suolo per attivare la ritenzione idrica immediata."
}, {
  id: 2,
  title: "Settimana 4: Ritenzione",
  icon: Flower2,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/water_retention_phase.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/water_retention_phase.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/water_retention_phase.png-wvc-sizes",
    alt: "Radici sane e idratate grazie a Solid Dew"
  },
  description: "Il terreno mantiene un'umidità costante, riducendo lo stress idrico delle piante anche in periodi di siccità."
}, {
  id: 3,
  title: "Sviluppo Radicale",
  icon: Sun,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/root_development.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/root_development.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/root_development.png-wvc-sizes",
    alt: "Sviluppo radicale potenziato"
  },
  description: "Le radici si espandono in un ambiente ottimale, assorbendo nutrienti in modo più efficiente e profondo."
}, {
  id: 4,
  title: "Raccolto: +25% Resa",
  icon: Scissors,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/increased_yield_harvest.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/increased_yield_harvest.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/increased_yield_harvest.png-wvc-sizes",
    alt: "Raccolto abbondante e di qualità superiore"
  },
  description: "Risultati tangibili: un incremento significativo della produzione e una qualità del frutto superiore."
}, {
  id: 5,
  title: "Sostenibilità Futura",
  icon: Truck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/sustainable_agriculture_future.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/sustainable_agriculture_future.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680532/img/sustainable_agriculture_future.png-wvc-sizes",
    alt: "Terreno rigenerato per le stagioni future"
  },
  description: "Un investimento che rigenera il suolo, garantendo fertilità e risparmio idrico per le generazioni a venire."
}];
export default function HarvestSeasonTimeline() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="262385" id="la-nostra-promessa" className="bg-background text-foreground py-24 overflow-hidden relative" ref={ref}>
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/10 blur-[120px] rounded-lg pointer-events-none" />

      <div className={`container mx-auto px-4 mb-16 text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="text-4xl md:text-5xl font-bold font-default mb-6 text-foreground tracking-tight">
          La Nostra <span className="text-primary">Promessa</span>
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto font-default text-xl md:text-2xl leading-relaxed">
          Dalla prima applicazione al raccolto finale, seguiamo ogni fase per garantire efficienza idrica e massima produttività.
        </p>
      </div>

      <div className={`w-full transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <ScrollArea className="w-full whitespace-nowrap pb-12">
          <div className="flex w-max min-w-full px-8 md:px-24 relative pt-4 pb-8 items-start justify-between gap-8 md:gap-16">
            
            {}
            {}
            <div className="absolute top-[200px] left-[176px] right-[176px] h-[2px] bg-border z-0 hidden md:block">
              <div className="h-full bg-ring transition-all duration-1500 ease-out" style={{
              width: isVisible ? '100%' : '0%'
            }} />
            </div>

            {stages.map((stage, i) => <div key={stage.id} data-index={i} className="relative z-10 flex flex-col items-center w-72 whitespace-normal text-center group">
                {}
                <div className="mb-8 overflow-hidden rounded-2xl shadow-lg border border-border bg-card transition-transform duration-500 group-hover:-translate-y-2">
                  <img src={stage.image.src} data-wvc-srcset={stage.image.srcset} data-wvc-sizes={stage.image.sizes} alt={stage.image.alt} loading="lazy" className="w-32 h-32 object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>

                {}
                <div className="relative w-full flex justify-center mb-8">
                  <div className="w-12 h-12 rounded-xl bg-card border-2 border-ring flex items-center justify-center text-ring shadow-md relative z-10 transition-colors duration-300 group-hover:bg-ring group-hover:text-secondary-foreground">
                    <stage.icon className="w-5 h-5" />
                  </div>
                </div>

                {}
                <h3 className="text-2xl font-bold font-default mb-4 text-foreground tracking-tight">
                  {stage.title}
                </h3>
                <p className="text-muted-foreground font-default text-lg leading-relaxed px-2">
                  {stage.description}
                </p>
              </div>)}
          </div>
          <ScrollBar orientation="horizontal" className="bg-border" />
        </ScrollArea>
      </div>
    </section>;
}